// Single source of truth for how organizer-authored Froala Page content is
// themed, shared by every renderer of that content:
//
//   * Bobcat Summary / Pages editors  -> Froala editor iframes (`.fr-iframe`)
//   * Public Event Website            -> static Page iframes (`.evw__page-frame`)
//
// THE RULE
//
//   Inline black and white are Eventene's THEME-DEFAULT foreground colours, not
//   organizer choices, so they follow the active theme. Every other colour is a
//   deliberate choice and is left exactly as authored.
//
// That is the product semantics Bobcat already ships:
// `DarkMode.updateIframeDarkmode()` rewrites editor content between
// `color: black` (light) and `color: white` (dark) as the theme changes, and
// Froala serialises whichever one was active at save time. So the same page can
// be stored as black or as white purely depending on when it was edited -- both
// must render correctly in both themes.
//
// WHY CSS RATHER THAN MORE INLINE REWRITING
//
// The existing helper only reaches the editor body's DIRECT children. Real
// content nests --
//
//   <div style="...color: black;"><h1 style="color: black;">…</h1>…</div>
//
// -- so the wrapper is recoloured while the heading keeps its own inline value
// and stays unreadable. Verified identically on Summary and in the Pages
// workspace. Matching the colour VALUE instead of the DOM position fixes it at
// any depth, and because it is CSS it can never be serialised back into an
// EventDescription. Editing and save behaviour are untouched.
//
// SELECTOR SHAPE
//
// `color` is matched only as a whole declaration -- at the start of the style
// attribute, or after a `; `. A naive `[style*="color: white"]` would also match
// `background-color: white` and `border-color: white`, which would then force
// white text onto a white fill in dark mode. Both real-world shapes are covered:
//
//   style="color: black;"                                  -> ^color:
//   style="font-size: 16px; font-family: Arial; color: black;" -> ; color:
//
// Short hex (`#000`, `#fff`) is deliberately NOT matched: as a substring it
// would also catch `#0000ff` and `#fff000`.
export const DARK_CLASS = 'froala-theme--dark';
export const LIGHT_CLASS = 'froala-theme--light';

const STYLE_ID = 'froala-theme-content';

// Foreground applied to theme-default content in each theme.
const DARK_FG = '#fff';
const LIGHT_FG = '#000';

// Values treated as theme defaults rather than organizer choices.
const THEME_DEFAULT_COLORS = [
  'black', 'rgb(0, 0, 0)', '#000000',
  'white', 'rgb(255, 255, 255)', '#ffffff',
];

function themeDefaultSelectors(themeClass) {
  return THEME_DEFAULT_COLORS
    .reduce((acc, value) => acc.concat([
      `html.${themeClass} [style^="color: ${value}" i]`,
      `html.${themeClass} [style*="; color: ${value}" i]`,
    ]), [])
    .join(',\n  ');
}

export const FROALA_THEME_CONTENT_CSS = `
  html.${DARK_CLASS} .fr-view,
  html.${DARK_CLASS}.fr-view {
    color: ${DARK_FG};
  }

  html.${LIGHT_CLASS} .fr-view,
  html.${LIGHT_CLASS}.fr-view {
    color: ${LIGHT_FG};
  }

  ${themeDefaultSelectors(DARK_CLASS)} {
    color: ${DARK_FG} !important;
  }

  ${themeDefaultSelectors(LIGHT_CLASS)} {
    color: ${LIGHT_FG} !important;
  }

  html.${DARK_CLASS} a {
    color: #8ab4f8;
  }

  html.${DARK_CLASS} table th,
  html.${DARK_CLASS} table td {
    border-color: rgba(255, 255, 255, .25);
  }

  html.${DARK_CLASS} table th {
    background: rgba(255, 255, 255, .12);
  }

  html.${DARK_CLASS} table.fr-alternate-rows tbody tr:nth-child(2n) {
    background: rgba(255, 255, 255, .06);
  }
`;

// Ensures `doc` carries the rules above and reflects `isDark`.
//
// Purely presentational: it injects a stylesheet and swaps a class on <html>.
// It never edits the content, so nothing here can be serialised back into an
// EventDescription.
export function applyFroalaThemeContent(doc, isDark) {
  if (!doc || !doc.documentElement) {
    return;
  }

  if (!doc.getElementById(STYLE_ID)) {
    const head = doc.head || doc.getElementsByTagName('head')[0];
    if (!head) {
      return;
    }
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = FROALA_THEME_CONTENT_CSS;
    head.appendChild(style);
  }

  // Exactly one theme class, so nothing applies until the theme is known and
  // there is no wrong-direction flash before this runs.
  doc.documentElement.classList.toggle(DARK_CLASS, !!isDark);
  doc.documentElement.classList.toggle(LIGHT_CLASS, !isDark);
}

export default applyFroalaThemeContent;
